import { faList } from '@fortawesome/pro-light-svg-icons';
import { faCalendarDays, faTableColumns, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppointmentListItem,
    BackendTabCount,
    Metric as MetricModel,
    ServiceCategoryCount,
    StaffListItem,
    StatusCount
} from '@spike/appointments-list-model';
import { Option, OWNER_ID, Period } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { CalendarFilter } from 'components/UI';
import SearchComponentPopup from 'components/UI/SearchComponentPopup';
import { Tabs } from 'components/UI/V2/Tabs/Tabs';
import Button from 'components/UI/V3/Button/Button';
import { useAuth } from 'hooks';
import moment from 'moment-timezone';
import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDebouncedCallback } from 'use-debounce';
import AddButton from './AddButton';
import { ServiceLocation } from '@spike/appointments-list-action';
import { ListType } from './AppointmentsList';
import styles from './AppointmentsListHeader.module.scss';
import AppointmentsExportCSV from './Export/AppointmentsExportCSV';
import Filter from './Filter/Filter';
import RoomFilter from './Filter/RoomFilter';
import ServiceTypeFilter from './Filter/ServiceTypeFilter';
import Input from './Input';
import OrganizationMarketplacesFilter from './OrganizationMarketplacesFilter';
import { Metrics } from './Metrics/Metrics';
import { isSessionListType, TabModel } from './tabs';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import startCase from 'lodash/startCase';
import WaitlistButton from 'components/Waitlist/WaitlistButton/WaitlistButton';
import { openWaitlistPanel, showWaitlistForm } from 'actions/waitlists/WaitlistsActions';
import { useDispatch } from 'react-redux';
import { WaitlistKey } from 'model/Waitlist';

interface AppointmentsListHeaderProps {
    listType: ListType;
    appointments: Array<AppointmentListItem>;
    selectedTabId: string | null;
    tabs: Array<TabModel>;
    period: Period;
    calendarSelectionId: string | null;
    search: string;
    selectedRoomUnitIds: Array<number>;
    countByStatus: Array<StatusCount>;
    countByServiceCategory: Array<ServiceCategoryCount>;
    countByBackendTab: Array<BackendTabCount>;
    countPendings: number;
    countIssues: number;
    countCheckedIn: number;
    countForSelectedTab: number;
    metrics: Array<MetricModel>;
    selectableRoomIds: Array<number>;
    selectableStaff: Array<StaffListItem>;
    selectedStaff: Array<StaffListItem>;
    selectedMarketplaceIds: Array<number>;
    selectedServiceLocation?: ServiceLocation;
    loading?: boolean;
    loadingMetrics?: boolean;
    className?: string;
    onSearch?: (search: string) => void;
    onChangePeriod?: (period: Period, allTime: boolean, calendarSelectionId: string | null) => void;
    onChangeTab?: (tabId: string) => void;
    onChangeRoomUnitIds?: (roomUnitIds: Array<number>) => void;
    onAdd?: (type: 'booking' | 'evaluation' | 'fastCheckIn') => void;
    bookingsView?: 'kanban' | 'list';
    onChangeBookingsView?: (view: 'kanban' | 'list') => void;
    onChangeStaff?: (selectedStaff: Array<StaffListItem>, all: boolean) => void;
    onChangeMarketplaces?: (selectedMarketplaceIds: Array<number>) => void;
    onChangeServiceCategories?: (selectedServiceCategories: Array<string>) => void;
    onChangeServiceLocation?: (serviceLocation: ServiceLocation | undefined) => void;
    hideDateFilter?: boolean;
    waitlistKey?: WaitlistKey;
}

const dateFormat = 'YYYY/MM/DD';

export const AppointmentsListHeader: FunctionComponent<AppointmentsListHeaderProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const auth = useAuth();
    const dispatch = useDispatch();

    const debounced = useDebouncedCallback(value => {
        props.onSearch?.(value);
    }, 500);

    const { getOrganizationServiceCategories } = useOrganizationMarketplaces();

    const serviceCategories: Array<Option<string>> = useMemo(
        () => getOrganizationServiceCategories().map(id => ({ id, name: startCase(id) })),
        []
    );
    const [selectedServiceCategories, setSelectedServiceCategories] = useState(serviceCategories);

    useEffect(() => {
        const ids = selectedServiceCategories.map(o => o.id);
        props.onChangeServiceCategories?.(ids.includes('grooming') ? [...ids, 'dental'] : ids);
    }, [selectedServiceCategories]);

    const isOwner = auth.user?.role.id === OWNER_ID;

    const [datePeriod, setDatePeriod] = useState<Period & { name: string | undefined }>({
        from: props.period.from,
        to: props.period.to,
        name: props.calendarSelectionId ?? undefined
    });

    useNonInitialEffect(() => {
        if (
            datePeriod.from.format(dateFormat) !== props.period.from.format(dateFormat) ||
            datePeriod.to.format(dateFormat) !== props.period.to.format(dateFormat) ||
            datePeriod.name !== props.calendarSelectionId
        ) {
            setDatePeriod(prev => ({
                ...prev,
                from: props.period.from,
                to: props.period.to,
                name:
                    props.calendarSelectionId ??
                    (moment().format(dateFormat) === props.period.from.format(dateFormat) &&
                    moment().format(dateFormat) === props.period.to.format(dateFormat)
                        ? 'Today'
                        : undefined)
            }));
        }
    }, [props.period, props.calendarSelectionId]);

    const changeSearchHanlder = (event: ChangeEvent<HTMLInputElement>) => {
        debounced(event.target.value);
    };

    const changePeriodHandler = (period: Period, allTime: boolean, opSelected?: string) => {
        setDatePeriod({
            ...period,
            name: opSelected || 'Today'
        });
        props.onChangePeriod?.({ ...period }, allTime, opSelected ?? null);
    };

    const changeTabHandler = (selectedTabId: string) => {
        props.onChangeTab?.(selectedTabId);
    };

    const changeRoomUnitsSelection = (roomUnitIds: Array<number>, all: boolean) => {
        props.onChangeRoomUnitIds?.(all ? [] : [...roomUnitIds]);
    };

    const changeStaffSelectionHandler = (selectedOptions: Array<Option<number>>) => {
        const selectedIds = selectedOptions.map(option => option.id);
        props.onChangeStaff?.(
            props.selectableStaff.filter(member => selectedIds.includes(member.id)),
            props.selectableStaff.length === 0 || props.selectableStaff.every(member => selectedIds.includes(member.id))
        );
    };

    const filtersAndCalendar = (
        <>
            {props.listType === 'daycare' && (
                <div className={styles.viewSwitch}>
                    <Button
                        onlyIcon
                        label="Kanban"
                        size="large"
                        variant={props.bookingsView === 'kanban' ? 'black' : 'outlined'}
                        leftIcon={faTableColumns}
                        className={styles.actionButton}
                        onClick={() => props.onChangeBookingsView?.('kanban')}
                    />
                    <Button
                        onlyIcon
                        label="List"
                        size="large"
                        variant={props.bookingsView === 'list' ? 'black' : 'outlined'}
                        leftIcon={faList}
                        className={styles.actionButton}
                        onClick={() => props.onChangeBookingsView?.('list')}
                    />
                </div>
            )}
            {props.listType === 'client' && serviceCategories.length > 1 && (
                <Filter
                    allLabel="All service categories"
                    quantityLabel="service categories"
                    icon={faList}
                    options={serviceCategories}
                    selectedOptions={selectedServiceCategories}
                    onChangeSelection={setSelectedServiceCategories}
                />
            )}
            <OrganizationMarketplacesFilter
                selected={props.selectedMarketplaceIds}
                onChangeSelection={props.onChangeMarketplaces}
            />
            {['daycare', 'boarding'].includes(props.listType) && props.selectableRoomIds.length > 0 && (
                <RoomFilter
                    selectableRoomIds={props.selectableRoomIds}
                    selectedRoomUnitIds={props.selectedRoomUnitIds}
                    onChangeSelection={changeRoomUnitsSelection}
                />
            )}
            {props.listType === 'all' && isOwner && (
                <AppointmentsExportCSV
                    appointments={props.appointments}
                    selectedMarketplaceIds={props.selectedMarketplaceIds}
                    disabled={props.appointments.length === 0}
                />
            )}
            {isSessionListType(props.listType) && (
                <ServiceTypeFilter
                    selected={props.selectedServiceLocation}
                    onChange={props.onChangeServiceLocation}
                />
            )}
            {(props.listType === 'grooming' || isSessionListType(props.listType)) &&
                props.selectableStaff.length > 0 &&
                props.onChangeStaff && (
                    <Filter
                        allLabel="All Staff"
                        quantityLabel="members"
                        icon={faUsers}
                        options={props.selectableStaff.map(staff => ({ id: staff.id, name: staff.name }))}
                        selectedOptions={props.selectedStaff}
                        onChangeSelection={changeStaffSelectionHandler}
                    />
                )}
            {!props.hideDateFilter && (
                <CalendarFilter
                    optionSelected={datePeriod.name}
                    buttonIcon={faCalendarDays}
                    period={datePeriod}
                    onChange={changePeriodHandler}
                    className={styles.datePicker}
                    hideAllTime={props.listType !== 'client'}
                    hideUpcoming={true}
                />
            )}
        </>
    );

    const addWaitlistHandler = () => {
        dispatch(openWaitlistPanel(props.waitlistKey ?? 'all', props.selectedMarketplaceIds));
        dispatch(showWaitlistForm());
    };

    const fastCheckInButton = (
        <Button
            label="+ Fast Check In"
            size={'medium'}
            fullWidth={isMobile}
            variant="black"
            onClick={() => props.onAdd?.('fastCheckIn')}
        />
    );

    return (
        <div className={clsx(styles.container, props.bookingsView === 'kanban' && styles.kanban, props.className)}>
            <div className={styles.fixRow}>
                <div className={styles.fixLeft}>
                    <div className={styles.searchContainer}>
                        <Input
                            value={props.search}
                            startIcon={<FontAwesomeIcon icon={faSearch} />}
                            placeholder="Search by clients or pets"
                            className={styles.search}
                            onChange={changeSearchHanlder}
                        />
                    </div>
                    <div className={styles.mobileSearchContainer}>
                        <span className={styles.mobileTitle}>Your Bookings</span>
                        <SearchComponentPopup
                            title="Your Bookings"
                            placeholder="Search by clients or pets"
                            onSearch={text => debounced(text)}
                        />
                    </div>
                </div>
                <div className={styles.fixRight}>
                    {!isMobile && filtersAndCalendar}
                    {props.waitlistKey && (
                        <WaitlistButton
                            waitlistKey={props.waitlistKey}
                            marketplaceIds={props.selectedMarketplaceIds}
                        />
                    )}
                    {props.onAdd && (
                        <AddButton
                            onAdd={props.onAdd}
                            onAddWaitlist={props.waitlistKey ? addWaitlistHandler : undefined}
                        />
                    )}
                </div>
            </div>
            {props.metrics.length > 0 && (
                <div className={styles.row}>
                    <Metrics
                        metrics={props.metrics}
                        loading={props.loadingMetrics}
                    />
                </div>
            )}
            {isMobile && props.listType === 'daycare' && <div className={styles.fixRow}>{fastCheckInButton}</div>}
            <div className={styles.row}>
                <div className={styles.mobileFiltersContaner}>
                    <div className={styles.left}>
                        <Tabs
                            value={props.selectedTabId ?? ''}
                            tabs={props.tabs.map(tab => ({
                                count:
                                    tab.id === props.selectedTabId
                                        ? props.countForSelectedTab
                                        : props.listType === 'requested'
                                        ? props.countByServiceCategory.reduce(
                                              (count, serviceCategoryCount) =>
                                                  count +
                                                  (tab.serviceCategory === 'all' ||
                                                  tab.serviceCategory === serviceCategoryCount.serviceCategory ||
                                                  (tab.serviceCategory === 'grooming' &&
                                                      ![
                                                          'boarding',
                                                          'daycare',
                                                          'evaluation',
                                                          'walking',
                                                          'training'
                                                      ].includes(serviceCategoryCount.serviceCategory))
                                                      ? serviceCategoryCount.count
                                                      : 0),
                                              0
                                          )
                                        : props.listType === 'boarding' &&
                                          !['boarding_pending', 'boarding_issue', 'boarding_checked_in'].includes(
                                              tab.id
                                          )
                                        ? props.countByBackendTab.reduce(
                                              (count, statusCount) =>
                                                  count +
                                                  ((tab.backendTabs ?? []).includes(statusCount.backendTab)
                                                      ? statusCount.count
                                                      : 0),
                                              0
                                          )
                                        : props.listType === 'boarding' && tab.id === 'boarding_checked_in'
                                        ? props.countCheckedIn
                                        : (props.listType === 'grooming' && tab.id === 'grooming_pending') ||
                                          (props.listType === 'daycare' && tab.id === 'daycare_pending') ||
                                          (props.listType === 'boarding' && tab.id === 'boarding_pending') ||
                                          (props.listType === 'evaluation' && tab.id === 'evaluation_pending') ||
                                          (props.listType === 'transportation' &&
                                              tab.id === 'transportation_pending') ||
                                          (props.listType === 'training' && tab.id === 'training_pending') ||
                                          (props.listType === 'walking' && tab.id === 'walking_pending')
                                        ? props.countPendings
                                        : (props.listType === 'grooming' && tab.id === 'grooming_issue') ||
                                          (props.listType === 'daycare' && tab.id === 'daycare_issue') ||
                                          (props.listType === 'boarding' && tab.id === 'boarding_issue') ||
                                          (props.listType === 'evaluation' && tab.id === 'evaluation_issue') ||
                                          (props.listType === 'transportation' && tab.id === 'transportation_issue') ||
                                          (props.listType === 'training' && tab.id === 'training_issue') ||
                                          (props.listType === 'walking' && tab.id === 'walking_issue')
                                        ? props.countIssues
                                        : props.countByStatus.reduce(
                                              (count, statusCount) =>
                                                  count +
                                                  (tab.statusIds.includes(statusCount.statusId)
                                                      ? statusCount.count
                                                      : 0),
                                              0
                                          ),
                                id: tab.id,
                                name: tab.name,
                                mark: tab.mark,
                                selectedColor: tab.selectedColor
                            }))}
                            loading={props.loading}
                            onChange={changeTabHandler}
                        />
                    </div>
                    {isMobile && <div className={styles.right}>{filtersAndCalendar}</div>}
                    {!isMobile && props.listType === 'daycare' && (
                        <div className={styles.right}>{fastCheckInButton}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppointmentsListHeader;
